import React, { useState, useEffect } from 'react';
import { Star, Trash2, Search, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import savedJobService, { SavedJob } from '../services/savedJobService';
import { getLocalSavedJobs, removeLocalSavedJob } from '../services/localSavedJobs';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import JobCard from '../components/Jobs/JobCard';
import { JobWithEmployer } from '../services/jobService';
import cvTemplateService from '../services/cvTemplateService';
import './SavedJobs.scss';

const SavedJobs: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');
  const [categoryImages, setCategoryImages] = useState<{ [value: string]: string | null }>({});
  const { showToast } = useToast();

  useEffect(() => {
    if (isLoading) return;
    fetchSavedJobs();

    Promise.all([
      cvTemplateService.getCategoryImages('company_activity'),
      cvTemplateService.getCategoryImages('work_field')
    ]).then(([activityImages, workFieldImages]) => {
      setCategoryImages({ ...workFieldImages, ...activityImages });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  const fetchSavedJobs = async () => {
    try {
      setLoading(true);

      if (!isAuthenticated) {
        const local = getLocalSavedJobs();
        const mapped: SavedJob[] = local.map(j => ({
          saved_id: j.id,
          saved_at: j.saved_at,
          id: j.id,
          title: j.title,
          company_name: j.company_name,
          location: j.location,
          salary_min: j.salary_min ?? undefined,
          salary_max: j.salary_max ?? undefined,
          job_type: j.job_type || undefined,
          work_time: j.work_time || undefined,
          employer_id: 0,
          created_at: j.created_at,
          updated_at: j.created_at,
        }));
        setSavedJobs(mapped);
        return;
      }

      const jobs = await savedJobService.getMySavedJobs();
      setSavedJobs(jobs);
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelectAll = () => {
    const filteredJobs = filterJobsByTitle(savedJobs);
    
    if (selectedJobs.length === filteredJobs.length && filteredJobs.length > 0) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(j => j.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedJobs.length === 0) return;

    const confirmMessage = `Ar tikrai norite pašalinti ${selectedJobs.length} skelbimų iš pažymėtų?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      if (!isAuthenticated) {
        selectedJobs.forEach(id => removeLocalSavedJob(id));
      } else {
        await Promise.all(
          selectedJobs.map(id => savedJobService.unsaveJob(id))
        );
      }

      setSavedJobs(savedJobs.filter(job => !selectedJobs.includes(job.id)));
      setSelectedJobs([]);
    } catch (error) {
      console.error('Error deleting saved jobs:', error);
      showToast('Įvyko klaida trinant skelbimus', 'error');
    }
  };

  const filterJobsByTitle = (jobs: SavedJob[]) => {
    if (!searchTitleFilter.trim()) return jobs;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return jobs.filter(job => 
      job.title.toLowerCase().includes(filterLower) ||
      job.company_name?.toLowerCase().includes(filterLower)
    );
  };

  if (loading) {
    return (
      <div className="saved-jobs-page">
        <div className="container">
          <div className="loading">Kraunama...</div>
        </div>
      </div>
    );
  }

  const filteredJobs = filterJobsByTitle(savedJobs);

  return (
    <div className="saved-jobs-page">
      
      <div className="container">
        {/* Header row: breadcrumbs left, filters right */}
        <div className="header-row">
          <div className="breadcrumbs">
            <span onClick={() => navigate('/')}>Pradžia</span>
            <span className="separator">&gt;</span>
            <span className="current">Pažymėti skelbimai</span>
          </div>
          <div className="filters-toolbar">
            {isAuthenticated && (
              <>
                <div className="select-all-dropdown">
                  <button 
                    className="select-all-btn"
                    onClick={handleToggleSelectAll}
                  >
                    <input 
                      type="checkbox" 
                      checked={selectedJobs.length > 0 && selectedJobs.length === filteredJobs.length}
                      onChange={handleToggleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Žymėti visus</span>
                  </button>
                </div>
                <button 
                  className="btn-delete"
                  onClick={handleDeleteSelected}
                  disabled={selectedJobs.length === 0}
                >
                  <Trash2 size={16} />
                  <span>Trinti</span>
                </button>
              </>
            )}
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Skelbimo pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={() => navigate('/darbo-skelbimai')}>
              <Plus size={16} />
              <span>Ieškoti skelbimų</span>
            </button>
          </div>
        </div>

        {filteredJobs.length === 0 ? (
          <div className="empty-state">
            <Star size={64} />
            <h2>{searchTitleFilter ? 'Skelbimų pagal šį filtrą nerasta' : 'Nėra pažymėtų skelbimų'}</h2>
            <p>{searchTitleFilter ? 'Pabandykite pakeisti paieškos kriterijus' : 'Kai pažymėsite darbo skelbimus, jie bus rodomi čia'}</p>
            {!searchTitleFilter && (
              <button onClick={() => navigate('/darbo-skelbimai')} className="browse-jobs-btn">
                Peržiūrėti skelbimus
              </button>
            )}
          </div>
        ) : (
          <div className="saved-jobs-grid">
            {filteredJobs.map((job) => {
              const jobData: JobWithEmployer = {
                id: job.id,
                title: job.title,
                company_name: job.company_name,
                location: job.location,
                salary_min: job.salary_min,
                salary_max: job.salary_max,
                job_type: job.job_type || '',
                work_time: job.work_time,
                description: job.description || '',
                employer_id: job.employer_id,
                company_logo: job.employer_logo,
                status: 'published' as const,
                created_at: job.created_at || '',
                updated_at: job.updated_at || '',
                work_field: job.work_field,
                company_activity: job.company_activity,
              };

              const companyActivity = job.company_activity;
              const workField = job.work_field;
              const categoryImageUrl = (companyActivity && categoryImages[companyActivity])
                || (workField && categoryImages[workField])
                || null;

              return (
                <JobCard
                  key={job.saved_id}
                  job={jobData}
                  layout="grid-3"
                  categoryImageUrl={categoryImageUrl}
                  disableViewedStyle
                  savedJobIds={new Set(savedJobs.map(j => j.id))}
                  onSaveToggle={(jobId, saved) => {
                    if (!saved) {
                      // JobCard already called the delete API — just update local state
                      setSavedJobs(prev => prev.filter(job => job.id !== jobId));
                    }
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedJobs;

